import { CarouselItem } from 'react-aria-carousel';
import { useQuery } from '@tanstack/react-query';
import {
  FloatingNextArrow,
  FloatingPrevArrow,
  PlayPauseToggle,
} from '@/components/CarouselControls';
import { InterruptionTile } from '@/components/InterruptionTile';
import { isInterruption, type RailItem } from '@/components/InterruptionTile/types';
import { ProductTile } from '@/components/ProductTile';
import { useDataLayer } from '@/hooks/useDataLayer';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useTrackItemList } from '@/hooks/useTrackItemList';
import { syncCarouselInert } from '@/utils/syncCarouselInert';
import type { Ga4Item, RecommendedProducts } from '@/types';
import { getData } from '@/utils/getData';
import { getUserId } from '@/utils/getUserId';
import { toGa4Item } from '@/utils/toGa4Item';
import { useEffect, useMemo, useRef } from 'react';
import * as Styled from './ProductCarousel.styled';

interface ProductCarouselProps {
  data: RecommendedProducts;
}

export const ProductCarousel = ({ data }: ProductCarouselProps) => {
  const isDesktop = useMediaQuery('(width >= 58rem)');
  const isWideScreen = useMediaQuery('(width >= 48rem)');
  const isSmallScreen = useMediaQuery('(width < 34rem)');
  const railRef = useRef<HTMLElement>(null);
  const listName = data.heading || 'product-carousel';
  const list = useMemo(
    () => ({
      name: data.heading || 'product-carousel',
      id: data.contentId ? String(data.contentId) : undefined,
    }),
    [data.heading, data.contentId],
  );

  // Fetch products from endpoint when no inline products are provided (Shopbox recommendations)
  const hasInlineProducts = (data.products ?? []).length > 0;
  const hasEndpoint = !!data.productsEndpoint?.trim();
  const userId = getUserId();

  const { data: fetchedData } = useQuery({
    enabled: !hasInlineProducts && hasEndpoint,
    queryKey: [data.key, data.productsEndpoint, data.productCode],
    queryFn: getData(data.productsEndpoint ?? '', {
      productCode: data.productCode ?? '',
      userId,
    }),
  });

  const desktopSlides = 5;
  const autoplay = data.autoplay ?? true;

  const getItemsPerPage = () => {
    if (isDesktop) return desktopSlides;
    if (isWideScreen) return 3.5;
    if (isSmallScreen) return 1.66;
    return 2.25;
  };

  const itemsPerPage = getItemsPerPage();
  const inlineProducts = (data.products ?? []) as RailItem[];
  const fetchedProducts = (fetchedData?.data?.products ?? []) as RailItem[];
  const items = hasInlineProducts ? inlineProducts : fetchedProducts;
  const productsLength = items.length;
  const enableScrolling = productsLength > itemsPerPage;
  const fewItems = productsLength <= desktopSlides;
  const shouldAutoplay = autoplay && !fewItems;

  // Stable object identity is required here. React diffs dangerouslySetInnerHTML
  // by reference, so a fresh literal makes it re-assign innerHTML on every commit,
  // replacing the description's text nodes. react-aria-carousel observes the
  // carousel subtree for childList changes and calls hasAttribute() on every
  // added/removed node, which throws on Text nodes and leaves its page/snap
  // model stale after the recommendations query resolves.
  const descriptionHtml = useMemo(
    () => (data.description ? { __html: data.description } : undefined),
    [data.description],
  );

  // GA4 product-list tracking: view_item_list on in-view + select_item
  // on tile click. Interruption cells are promotions, tracked separately.
  const ga4Items = useMemo(
    () =>
      items
        .map((item, index) =>
          isInterruption(item) ? null : toGa4Item(item, list, index),
        )
        .filter((item): item is Ga4Item => item !== null),
    [items, list],
  );
  const { onSelectItem } = useTrackItemList(railRef, list, ga4Items);
  const { mediaInteract } = useDataLayer();
  const emptyRef = useRef<HTMLSpanElement>(null);

  // #2934 A11Y-05 - off-screen slides must be inert AND aria-hidden together, while
  // partially visible peek tiles stay interactive. See syncCarouselInert for why neither
  // react-aria's default nor the previous blanket strip was correct.
  const scrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = scrollerRef.current;

    if (!node) return;

    return syncCarouselInert(node);
  }, []);

  // Clear CLS min-height on the mount point when there are no products to display
  useEffect(() => {
    if (productsLength === 0 && emptyRef.current) {
      const mount = emptyRef.current.closest('[data-react-component]') as HTMLElement;
      if (mount) mount.style.minHeight = '0';
    }
  }, [productsLength]);

  return productsLength > 0 ? (
      <Styled.Wrapper key={data.key} className={data.surface ?? 'surface-white'} ref={railRef}>
      <Styled.Carousel
        aria-label={`Product recommendations: "${data.heading}"`}
        autoplay={shouldAutoplay}
        autoplayInterval={4000}
        itemsPerPage={itemsPerPage}
        scrollBy="item"
        loop={enableScrolling ? 'infinite' : false}
        scrollPadding="5px"
        // autoplay fires a page change every tick, which isn't user
        // engagement — only track when autoplay is off. Upgrade to full
        // coverage needs a user-gesture guard to tell auto-advance from manual.
        onActivePageIndexChange={
          autoplay
            ? undefined
            : ({ index }) => mediaInteract('product-carousel', 'navigate', index)
        }
        spaceBetweenItems={isWideScreen ? '24px' : '12px'}
      >
        <Styled.Inner $noAutoplay={!shouldAutoplay}>
          <Styled.Head>
            {data.heading ? (
              <Styled.TitleRow>
                <Styled.Title>{data.heading}</Styled.Title>
                <Styled.Rule aria-hidden="true" />
              </Styled.TitleRow>
            ) : null}
            <Styled.SubRow>
              {descriptionHtml ? (
                // biome-ignore lint/security/noDangerouslySetInnerHtml: CMS editor content (XhtmlString)
                <Styled.Description
                  $noAutoplay={!shouldAutoplay}
                  dangerouslySetInnerHTML={descriptionHtml}
                />
              ) : null}
              {data.viewAllLink ? (
                <Styled.SubRowActions>
                  <Styled.ViewAllLink href={data.viewAllLink}>
                    {data.viewAllText || 'View all'}
                  </Styled.ViewAllLink>
                </Styled.SubRowActions>
              ) : null}
            </Styled.SubRow>
          </Styled.Head>
        </Styled.Inner>

        <Styled.Track>
          {shouldAutoplay ? <Styled.PlayPauseWrapper><PlayPauseToggle /></Styled.PlayPauseWrapper> : null}
          {enableScrolling && !fewItems && isWideScreen ? <FloatingPrevArrow /> : null}
          <Styled.Main ref={scrollerRef}>
            {items.map((item: RailItem, index: number) =>
              isInterruption(item) ? (
                <CarouselItem key={item.key}>
                  <InterruptionTile data={item} />
                </CarouselItem>
              ) : (
                <CarouselItem key={index}>
                  <ProductTile
                    hideCta={data.showAddToCart === false}
                    hidePromotion={data.showDispatchMessage === false}
                    showMoreLikeThis={data.showMoreLikeThis}
                    index={index}
                    listName={listName}
                    product={item}
                    productNameElement="h3"
                    onSelect={() => onSelectItem(toGa4Item(item, list, index), index)}
                  />
                </CarouselItem>
              ),
            )}
          </Styled.Main>
          {enableScrolling && !fewItems && isWideScreen ? <FloatingNextArrow /> : null}
        </Styled.Track>
      </Styled.Carousel>
    </Styled.Wrapper>
  ) : <span ref={emptyRef} hidden />;
};
